import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, DeleteCommand, standardCommand } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { DeleteCommandHandler } from '../../../core/service/command/delete-command-handler.service';
import { DeleteCommandHandlerV2 } from '../../../core/service/command/delete-command-handler.v2.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { AmortizationPlanDocument } from '../../model/amortization-plan';
import { AmortizationPlanDocumentService } from '../../service/data/amortization-plan-document.service';

@Component({
  standalone: false,
  selector: 'app-amortization-plan-document-manager',
  templateUrl: './amortization-plan-document-manager.component.html',
  providers: [NavigationService, StandardCommandHandlerResolver, { provide: DeleteCommandHandler, useClass: DeleteCommandHandlerV2 }],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AmortizationPlanDocumentManagerComponent extends CommandController implements OnInit {
  public readonly contextId: string = 'cmd-' + Math.random().toString(36).slice(2);

  @Input() amortizationPlanId?: string;
  @Input() canManage: boolean = false;

  @Output() countChange = new EventEmitter<number>();

  columnDefinitions: ColumnDefinition[];
  contextMenuDefinitions: MenuItemDefinition[];
  items: AmortizationPlanDocument[] = [];

  constructor(
    private readonly _dataService: AmortizationPlanDocumentService,
    private readonly _nativeWindow: NativeWindowService,
    cdf: ColumnDefinitionFactory,
    commandHandlerResolver: StandardCommandHandlerResolver,
    dispatcher: CommandDispatcher
  ) {
    super(dispatcher, commandHandlerResolver);
    this.columnDefinitions = [
      cdf.create('LEASE_AGREEMENT_DOCUMENT.CAPTION', 'caption'),
      cdf.createDateTime('LEASE_AGREEMENT_DOCUMENT.UPLOADED_DATE', 'uploadedDate'),
      cdf.createNumber('LEASE_AGREEMENT_DOCUMENT.SIZE', 'size', ColumnVisibility.optional),
    ];
    this.contextMenuDefinitions = [new MenuItemDefinition('DOWNLOAD_BTN', standardCommand.download)];
  }

  ngOnInit(): void {
    if (this.canManage) {
      this.contextMenuDefinitions.push(new MenuItemDefinition('DELETE_BTN', standardCommand.delete));
    }

    this.refresh();
  }

  canExecute(command: CommandBase): boolean {
    const cmd = command as Command;
    if (cmd?.contextId && cmd.contextId !== this.contextId) {
      return false;
    }

    switch (command.id) {
      case standardCommand.delete:
        return this.canManage && command instanceof Command && !!command.context;
      case standardCommand.download:
        return command instanceof Command && !!command.context;
      default:
        return super.canExecute(command);
    }
  }

  execute(command: CommandBase): void {
    if (!(command instanceof Command)) {
      return;
    }

    switch (command.id) {
      case standardCommand.delete:
        if (!this.canManage) {
          return;
        }

        const deleteCommand = command as DeleteCommand;
        deleteCommand.itemsSource = this.items;
        deleteCommand.dataService = this._dataService;
        super.execute(deleteCommand);
        break;

      case standardCommand.download:
        this.AddSubscription(
          this._dataService.download(command.context).subscribe(res => {
            this._nativeWindow.save(res);
          })
        );
        break;
    }
  }

  onRowClicked(e: { context: AmortizationPlanDocument; event: any }): void {
    this.AddSubscription(
      this._dataService.download(e.context).subscribe(res => {
        this._nativeWindow.save(res);
      })
    );
  }

  refresh(): void {
    if (!this.amortizationPlanId) {
      this.items = [];
      this.countChange.emit(0);
      return;
    }

    this.AddSubscription(
      this._dataService.getByAmortizationPlan(String(this.amortizationPlanId)).subscribe(res => {
        this.items = res;
        this.countChange.emit(this.items?.length ?? 0);
      })
    );
  }

  protected executedWithResult(result: boolean, command: CommandBase): void {
    if (result && command?.id === standardCommand.delete) {
      this.countChange.emit(this.items?.length ?? 0);
    }
  }
}
